import React, { useId, useRef, useState } from 'react';
import { RiArrowUpLine, RiCheckboxCircleLine, RiFileList3Line, RiArrowRightSLine, RiPriceTag3Line, RiEyeLine, RiDeleteBinLine, RiCheckboxCircleFill } from '@remixicon/react';
import { cn } from '../../../../components/ui/utils';
import { Input } from '../../../../components/ui/Input';
import { Textarea } from '../../../../components/ui/Textarea';
import PriceBreakdownDrawer from './PriceBreakdownDrawer';

/**
 * QuoteSettingsSection Component
 * Card-style form section for quote metadata (title, dates, notes)
 *
 * @param {Object} props
 * @param {Object} props.settings - Quote settings object
 * @param {Function} props.onUpdate - Callback when settings update
 * @param {boolean} props.isComplete - Whether section is complete
 * @param {boolean} props.isExpanded - Whether section is expanded
 * @param {Function} props.onToggle - Callback when section is toggled
 * @param {Function} props.onPreview - Callback when Preview Quote button is clicked
 * @param {Function} props.onBack - Callback when Back button is clicked
 * @param {boolean} props.isAircraftComplete - Whether aircraft section is complete (prerequisite)
 * @param {boolean} props.isUnlocked - Whether section has been unlocked by clicking Next
 * @param {boolean} props.disabled - Whether section is disabled
 * @param {Object} props.errors - Validation errors object
 * @param {Array} props.flightOptions - Array of flight options for price calculation
 */
const QuoteSettingsSection = ({
  settings = {
    bidTitle: '',
    validUntil: '',
    expirationDate: '',
    notes: '',
  },
  onUpdate,
  isComplete = false,
  isExpanded = false,
  onToggle,
  onPreview,
  onBack,
  isAircraftComplete = false,
  isUnlocked = false,
  disabled = false,
  errors = {},
  flightOptions = [],
  defaultContractUrl = null,
}) => {
  const titleId = useId();
  const validUntilId = useId();
  const notesId = useId();
  const titleErrorId = useId();
  const validUntilErrorId = useId();

  const [canProceed, setCanProceed] = useState(false);
  const [priceBreakdownOpen, setPriceBreakdownOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Handle contract file selection (upload deferred to quote generation)
  const handleContractUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleChange('contractFile', { name: file.name, size: file.size, file });
  };

  // Handle field change
  const handleChange = (field, value) => {
    if (onUpdate) {
      const updatedSettings = {
        ...settings,
        [field]: value,
      };
      console.log('Quote settings updated:', updatedSettings);
      const hasContract = (updatedSettings.contractFile && updatedSettings.contractFile !== 'removed') || (defaultContractUrl && updatedSettings.contractFile !== 'removed');
      setCanProceed(updatedSettings.bidTitle && updatedSettings.validUntil && hasContract);
      onUpdate(updatedSettings);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayString = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Safe settings access
  const safeSettings = settings || {
    bidTitle: '',
    validUntil: '',
    expirationDate: '',
    notes: '',
  };

  return (
  <>
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
      {/* Section Header - Always Visible */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled || !isUnlocked}
        className={cn(
          'w-full flex items-center justify-between p-6 text-left transition-colors',
          (disabled || !isUnlocked) && 'cursor-not-allowed opacity-60'
        )}
      >
        <div className="flex items-center gap-3 flex-1">
          <RiFileList3Line className="w-5 h-5 text-gray-700 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-gray-900">Quote Settings</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {isComplete && (
            <div className="flex items-center justify-center w-6 h-6 rounded-full">
              <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
            </div>
          )}
        </div>
      </button>

      {/* Section Content - Shown when expanded */}
      {isExpanded && (
        <div className="px-6 pb-6">
          {/* Form Content */}
          <form
        data-testid="quote-settings-form"
        aria-label="Quote settings form"
        className="space-y-4"
        onSubmit={(e) => e.preventDefault()}
      >
        {/* Quote Title */}
        <div>
          <label
            htmlFor={titleId}
            className="block text-xs font-medium text-gray-600 mb-1.5"
          >
            Quote Title *
          </label>
          <Input
            id={titleId}
            type="text"
            value={safeSettings.bidTitle || ''}
            onChange={(e) => handleChange('bidTitle', e.target.value)}
            placeholder="e.g., Miami Trip (TEB → MIA)"
            maxLength={100}
            disabled={disabled}
            aria-required="true"
            aria-invalid={errors.bidTitle ? 'true' : 'false'}
            aria-describedby={errors.bidTitle ? titleErrorId : undefined}
            className={cn(
              errors.bidTitle && 'border-red-500',
              disabled && 'opacity-60 cursor-not-allowed'
            )}
          />
          {errors.bidTitle && (
            <p id={titleErrorId} className="mt-1 text-xs text-red-600">
              {errors.bidTitle}
            </p>
          )}
        </div>

        {/* */}
        <div className="grid grid-cols-2 gap-4">
         
          <div>
            <label
              htmlFor={validUntilId}
              className="block text-xs font-medium text-gray-600 mb-1.5"
            >
              Expiration Date *
            </label>
            <input
              id={validUntilId}
              data-testid="date-input-valid-until"
              type="date"
              value={safeSettings.validUntil}
              onChange={(e) => handleChange('validUntil', e.target.value)}
              onInput={(e) => handleChange('validUntil', e.target.value)}
              min={getTodayString()}
              disabled={disabled}
              required
              aria-required="true"
              aria-invalid={errors.validUntil ? 'true' : 'false'}
              aria-describedby={errors.validUntil ? validUntilErrorId : undefined}
              className={cn(
                'w-full px-3 py-2.5 border rounded-lg text-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.validUntil ? 'border-red-500' : 'border-gray-300 hover:border-gray-400',
                disabled && 'bg-gray-100 cursor-not-allowed opacity-60'
              )}
            />
            {errors.validUntil && (
              <span
                id={validUntilErrorId}
                data-testid="error-valid-until"
                className="block mt-1 text-xs text-red-600"
              >
                {errors.validUntil}
              </span>
            )}
          </div>

          {/*
          <div>
            <label
              htmlFor={expirationId}
              className="block text-xs font-medium text-gray-600 mb-1.5"
            >
              Expiration Date
            </label>
            <input
              id={expirationId}
              data-testid="date-input-expiration-date"
              type="date"
              value={safeSettings.expirationDate || ''}
              onChange={(e) => handleChange('expirationDate', e.target.value)}
              min={getTodayString()}
              disabled={disabled}
              aria-required="false"
              aria-invalid={errors.expirationDate ? 'true' : 'false'}
              aria-describedby={errors.expirationDate ? expirationErrorId : undefined}
              className={cn(
                'w-full px-3 py-2.5 border rounded-lg text-sm transition-all',
                'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500',
                errors.expirationDate ? 'border-red-500' : 'border-gray-300 hover:border-gray-400',
                disabled && 'bg-gray-100 cursor-not-allowed opacity-60'
              )}
            />
            {errors.expirationDate && (
              <span
                id={expirationErrorId}
                data-testid="error-expiration-date"
                className="block mt-1 text-xs text-red-600"
              >
                {errors.expirationDate}
              </span>
            )}
          </div> Expiration Date */}
        </div>

        {/* Notes */}
        <div>
          <label
            htmlFor={notesId}
            className="block text-xs font-medium text-gray-600 mb-1.5"
          >
            Message to Client
          </label>
          <Textarea
            id={notesId}
            value={safeSettings.notes || ''}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Add a personalized message to your client..."
            rows={4}
            maxLength={1000}
            disabled={disabled}
            className={cn(
              'w-full',
              disabled && 'opacity-60 cursor-not-allowed'
            )}
          />
        </div>

        {/* Contract PDF Upload */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1.5">
            Standard Charter Contract PDF *
          </label>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            disabled={disabled}
            onChange={handleContractUpload}
          />
          {safeSettings.contractFile && safeSettings.contractFile !== 'removed' ? (
            <div className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-3">
              <div className="flex items-center justify-center w-9 h-9 bg-red-50 rounded-lg shrink-0">
                <span className="text-[10px] font-bold text-red-500">PDF</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{safeSettings.contractFile.name}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {safeSettings.contractFile.size ? `${(safeSettings.contractFile.size / 1024).toFixed(0)} KB · ` : ''}
                  <span className="inline-flex items-center gap-0.5 text-green-600">
                    <RiCheckboxCircleFill className="w-3 h-3" /> Completed
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  handleChange('contractFile', 'removed');
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <RiDeleteBinLine className="w-4 h-4" />
              </button>
            </div>
          ) : defaultContractUrl && safeSettings.contractFile !== 'removed' ? (
            <div className="flex items-center gap-3 border border-gray-200 rounded-lg px-3 py-3">
              <div className="flex items-center justify-center w-9 h-9 bg-red-50 rounded-lg shrink-0">
                <span className="text-[10px] font-bold text-red-500">PDF</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {decodeURIComponent(defaultContractUrl.split('/').pop().split('?')[0]) || 'standardcharter_contract.pdf'}
                </p>
                <p className="text-xs text-gray-700 mt-0.5">
                  <span className="inline-flex items-center gap-0.5">
                    <RiCheckboxCircleFill className="w-3 h-3 text-green-400" /> Completed
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleChange('contractFile', 'removed')}
                className="shrink-0 p-1.5 text-gray-400 hover:text-red-500 transition-colors"
              >
                <RiDeleteBinLine className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div
              onClick={() => !disabled && fileInputRef.current?.click()}
              className={cn(
                'flex items-center justify-between border border-gray-300 rounded-lg px-3 py-3 cursor-pointer hover:border-gray-400 transition-colors',
                disabled && 'opacity-60 cursor-not-allowed bg-gray-100'
              )}
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-900 truncate">Choose a file</p>
                <p className="text-xs text-gray-400 mt-0.5">PDF, up to 50 MB.</p>
              </div>
              <span className="shrink-0 ml-3 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                Browse File
              </span>
            </div>
          )}
        </div>
      </form>

      {/* Navigation Buttons */}
      <div className="mt-6">
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onBack}
            disabled={disabled}
            className={cn(
              'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
              'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
              'flex items-center justify-center gap-2',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <RiArrowUpLine className="w-4 h-4" />
            Back
          </button>
          <button
            type="button"
            onClick={onPreview}
            disabled={!canProceed}
            className={cn(
              'flex-1 px-4 py-3 rounded-full text-sm font-medium transition-all',
                'bg-accent text-primary-500 hover:bg-accent',
                'focus:outline-none focus:ring-0 focus:ring-blue-0 focus:ring-offset-0',
                'flex items-center justify-center gap-2',
              (!canProceed) && 'opacity-50 cursor-not-allowed'
            )}
          >
            Preview Quote  <RiEyeLine className="w-4 h-4" />
          </button>
        </div>
      </div>

        </div>
      )}
    </div>

    {/* Average Price Section */}
    {isExpanded && flightOptions.length > 0 && (() => {
      const totalPrice = flightOptions.reduce((sum, opt) => {
        // Prefer saved operator_cost; fall back to total_price for first-load
        // (before any edit has been saved, total_price IS the operator cost).
        const savedOpCost = parseFloat(opt.operator_cost);
        const operatorCost = !isNaN(savedOpCost) && savedOpCost > 0
          ? savedOpCost
          : (parseFloat(opt.total_price) || 0);
        const brokerMarkup = parseFloat(opt.broker_markup) || 0;
        const federalExciseTax = parseFloat(opt.federal_excise_tax) || 0;
        const platformFee = parseFloat(opt.platform_fee) || 0;
        const segmentTax = parseFloat(opt.segment_tax) || 0;
        return sum + operatorCost + brokerMarkup + federalExciseTax + platformFee + segmentTax;
      }, 0);
      const avgPrice = totalPrice / flightOptions.length;
      return (
        <div
          className="bg-white rounded-2xl shadow-lg shadow-gray-300/50 border border-gray-200 p-2 flex items-center justify-between cursor-pointer hover:border-gray-300 transition-colors"
          onClick={() => setPriceBreakdownOpen(true)}
        >
          <div>
            <p className="text-sm font-medium text-gray-900">Average Price</p>
            <p className="text-xs text-gray-500">Across {flightOptions.length} flight option{flightOptions.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 !text-lg text-primary-500 font-normal bg-accent rounded-lg px-3">
              <RiPriceTag3Line className="w-4 h-4" />
              <span>${avgPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
            <RiArrowRightSLine className="w-4 h-4 text-primary-500" />
          </div>
        </div>
      );
    })()}

    {/* Price Breakdown Drawer */}
    <PriceBreakdownDrawer
      isOpen={priceBreakdownOpen}
      onClose={() => setPriceBreakdownOpen(false)}
      flightOptions={flightOptions}
    />

    </>
  );
};

export default QuoteSettingsSection;
